// ProgressPill — a small "{done} of {total}" pill with a donut ring, used on
// process task rows (offboarding) to show how far along they are.
// design ref: styles.css:1000 (.progress-pill / .progress-pill__circle) +
// tasks.jsx ProgressRing.

import { Box, HStack } from "@chakra-ui/react";

const ProgressRing = ({ done, total, size = 16 }: { done: number; total: number; size?: number }) => {
  const pct = total > 0 ? Math.min(1, done / total) : 0;
  const stroke = 2.5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const c = size / 2;

  return (
    <Box as="svg" width={`${size}px`} height={`${size}px`} viewBox={`0 0 ${size} ${size}`} flexShrink={0}>
      {/* track — current color at low opacity */}
      <Box
        as="circle"
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.22}
        strokeWidth={stroke}
      />
      {/* progress arc — sweeps clockwise from 12 o'clock */}
      <Box
        as="circle"
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - pct)}
        transform={`rotate(-90 ${c} ${c})`}
      />
    </Box>
  );
};

export interface ProgressPillProps {
  done: number;
  total: number;
}

const ProgressPill = ({ done, total }: ProgressPillProps) => {
  const complete = total > 0 && done >= total;
  return (
    <HStack
      as="span"
      spacing="1xs"
      pl="4px"
      pr="10px"
      py="3px"
      bg="neutral.100"
      borderRadius="full"
      fontSize="xs"
      fontWeight={600}
      lineHeight="1"
      color={complete ? "success.700" : "p.700"}
      flexShrink={0}
    >
      <ProgressRing done={done} total={total} />
      <Box as="span">
        {done} of {total}
      </Box>
    </HStack>
  );
};

export default ProgressPill;
